import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Box, Stack, TextField, Typography } from "@mui/material";
import BaseDialog from "./Framework/BaseDialog";
import supabase from "../../../../utils/supabase";

const trim = (f) => ({
  id: String(f.id || "").trim(),
  issn: String(f.issn || "").trim(),
  publisher: String(f.publisher || "").trim(),
  name_english: String(f.name_english || "").trim(),
  name_original: String(f.name_original || "").trim(),
});

// Safe UUID generator: prefer crypto.randomUUID when available
const makeUuid = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  // fallback
  return `j_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

// Simple slugify for journal slug column
const slugify = (s = "") =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[^ -]/g, "") // strip non-ascii to keep slug simple
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

// Ensure slug uniqueness by appending numeric suffix when necessary
const makeUniqueSlug = async (base) => {
  if (!base) base = `journal-${Date.now().toString(36)}`;
  let slug = base;
  let counter = 0;
  while (true) {
    const { data, error } = await supabase.from("journals").select("id").eq("slug", slug).limit(1);
    if (error) throw error;
    if (!data || data.length === 0) return slug;
    counter += 1;
    slug = `${base}-${counter}`;
    // avoid infinite loops: after many tries, append random suffix
    if (counter > 10) slug = `${base}-${Math.random().toString(36).slice(2, 6)}`;
  }
};

/**
 * Journal 追加/編集 共通ダイアログ（BaseDialog 使用）
 *
 * props:
 * - open: boolean
 * - mode?: "add" | "edit"  // journal があれば自動的に "edit"
 * - journal?: { id, issn, publisher, name_english, name_original }
 * - onClose?: (saved?: boolean) => void  // 保存時 true
 * - onSaved?: (row) => void              // 追加/更新後のレコードを返す
 */
export default function DialogJournalUpsert({
  open,
  mode,
  journal,
  onClose,
  onSaved,
}) {
  const isEdit = useMemo(() => (mode ? mode === "edit" : !!journal), [mode, journal]);

  // For new journals we will auto-generate an UUID (stored in `id`) so the user
  // does not need to provide it. Keep it in state but do not show an input for it.
  const [form, setForm] = useState({ id: "", issn: "", publisher: "", name_english: "", name_original: "" });
  const [errors, setErrors] = useState({ name_english: false, name_original: false });
  const [loading, setLoading] = useState(false);

  const initialRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    // journal rows may use DB column names (name_eng/name_org) or older names
    const base = isEdit && journal
      ? {
          id: journal.id,
          issn: journal.issn || "",
          publisher: journal.publisher || "",
          name_english: journal.name_eng ?? journal.name_english ?? "",
          name_original: journal.name_org ?? journal.name_original ?? "",
          // keep slug if present in edit mode (we don't expose it in UI)
          slug: journal.slug || null,
        }
      : { id: makeUuid(), issn: "", publisher: "", name_english: "", name_original: "", slug: null };

    setForm(base);
    setErrors({ name_english: false, name_original: false });
    // Record the initial state ignoring the autogenerated id so that
    // the dialog isn't immediately considered dirty due to id presence.
    const initialNoId = trim({ ...base, id: "" });
    initialRef.current = JSON.stringify(initialNoId);
  }, [open, isEdit, journal]);

  const isDirty = useMemo(() => {
    if (!initialRef.current) return false;
    // ignore id when checking dirty-state since id is autogenerated for new items
    const copy = { ...form, id: "" };
    return JSON.stringify(trim(copy)) !== initialRef.current;
  }, [form]);

  const setField = useCallback((k, v) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((e) => ({ ...e, [k]: false }));
  }, []);

  const validate = useCallback((f) => {
    // Require at least one name (English or Original). DB allows both nullable,
    // but having neither is not useful for users.
    const missing = !(f.name_english || f.name_original);
    const next = {
      name_english: missing && !f.name_english,
      name_original: missing && !f.name_original,
    };
    setErrors(next);
    return !missing;
  }, []);

  const handleSubmit = useCallback(async () => {
    let payload = trim(form);
    if (!validate(payload)) return;
    setLoading(true);
    try {
      if (!isEdit) {
        // Prepare DB payload: map UI fields to DB column names (name_eng/name_org), add slug
        if (!payload.id) payload.id = makeUuid();
        const baseSlug = slugify(payload.name_english || payload.name_original || payload.id || "journal");
        const slug = await makeUniqueSlug(baseSlug);
        const dbPayload = {
          id: payload.id,
          issn: payload.issn || null,
          publisher: payload.publisher || null,
          name_eng: payload.name_english || null,
          name_org: payload.name_original || null,
          slug,
        };
        const { data: inserted, error: insErr } = await supabase.from("journals").insert([dbPayload]).select().single();
        if (insErr) throw insErr;
        onSaved?.(inserted);
      } else {
        // Update: do not change slug by default; map fields to DB column names
        const dbUpdate = {
          issn: payload.issn || null,
          publisher: payload.publisher || null,
          name_eng: payload.name_english || null,
          name_org: payload.name_original || null,
        };
        const { data: updated, error: upErr } = await supabase
          .from("journals")
          .update(dbUpdate)
          .eq("id", journal.id)
          .select()
          .single();
        if (upErr) throw upErr;
        onSaved?.(updated);
      }
      onClose?.(true);
    } catch (e) {
      console.error(e);
      alert(`Failed to ${isEdit ? "update" : "add"} journal: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [form, isEdit, journal, onClose, onSaved, validate]);

  const headerAddon = (
    <Box sx={{ px: 2.5, py: 1.25, display: "flex", gap: 2, alignItems: "center" }}>
      <Typography variant="body2" sx={{ opacity: 0.75 }}>
        {isEdit ? "Editing existing journal" : "Create a new journal"}
      </Typography>
      <Box sx={{ ml: "auto", fontSize: 12, opacity: 0.65 }}>
        Display name:&nbsp;
        <strong>{form.name_english || form.name_original || form.code || "(no name)"}</strong>
      </Box>
    </Box>
  );

  return (
    <BaseDialog
      open={open}
      title={isEdit ? "Edit Journal" : "Add New Journal"}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitText={isEdit ? "Update" : "Add"}
      loading={loading}
      confirmCloseWhen={!loading && isDirty}
      confirmCloseMessage="You have unsaved changes. Close without saving?"
      headerAddon={headerAddon}
      maxWidth="sm"
      fullWidth
    >
      <Stack spacing={1.5}>
          {/* ID is auto-generated for new journals and not shown to the user */}
        <TextField label="ISSN" value={form.issn} onChange={(e) => setField("issn", e.target.value)} fullWidth />
        <TextField label="Publisher" value={form.publisher} onChange={(e) => setField("publisher", e.target.value)} fullWidth />
        <TextField
          label="Journal Name (English)"
          value={form.name_english}
          onChange={(e) => setField("name_english", e.target.value)}
          fullWidth
          error={errors.name_english}
          helperText={
            errors.name_english ? "Provide at least one name (English or Original)." : "At least one of English or Original name is required."
          }
        />
        <TextField
          label="Journal Name (Original Language)"
          value={form.name_original}
          onChange={(e) => setField("name_original", e.target.value)}
          fullWidth
          error={errors.name_original}
          helperText={
            errors.name_original ? "Provide at least one name (English or Original)." : "At least one of English or Original name is required."
          }
        />
      </Stack>
    </BaseDialog>
  );
}
